import { CONFIG } from '../config.js';
import { filterPool, pickRandom, pickWeighted, shuffle } from '../data/pools.js';
import { getOptions } from '../runtime.js';
import {
  getEligibleForReview,
  getReviewWeight,
  hasReviewableData,
  recordBest,
  recordResult,
  recordRoundComplete,
} from '../storage.js';
import type { Country, EffectiveQuizMode, Question, QuizState } from '../types.js';
import type { GlobeView } from './globeSetup.js';

export interface QuizCallbacks {
  onStateChange: (state: QuizState) => void;
  onAsk: (q: Question, info: { modeChanged: boolean }) => void;
  onResult: (correct: boolean, picked: Country, answer: Country) => void;
  onFinish: (state: QuizState) => void;
  /** 타이머 게이지용. 0~1로 남은 비율. enabled 일 때만 호출. */
  onTimerTick?: (remaining: number) => void;
  /** 위치 모드에서 보기 외 폴리곤을 클릭했을 때 호출. */
  onMisclick?: () => void;
  /** 잔여 시간이 정수 초 경계(3→2→1)를 넘는 순간 호출. enabled 일 때만. */
  onCountdown?: (secondsLeft: number) => void;
}

export class GlobeQuiz {
  private state: QuizState;
  private pool: Country[] = [];
  private reviewMode = false;
  private modeSequence: EffectiveQuizMode[] = [];
  private readonly view: GlobeView;
  private readonly callbacks: QuizCallbacks;
  private timerHandle: number | null = null;
  private timerStart = 0;
  private timeLimitSecondsAtStart = 0;
  private lastWholeSecond = -1;
  private pendingAdvance: number | null = null;

  constructor(view: GlobeView, callbacks: QuizCallbacks) {
    this.view = view;
    this.callbacks = callbacks;
    this.state = this.initialState();
  }

  private initialState(): QuizState {
    return {
      phase: 'idle',
      questionIndex: 0,
      totalQuestions: CONFIG.questionsPerRound,
      score: 0,
      wrongCount: 0,
      combo: 0,
      maxCombo: 0,
      current: null,
      currentMode: 'name',
      history: [],
    };
  }

  getState(): QuizState {
    return this.state;
  }

  start(): void {
    const opts = getOptions();
    this.reviewMode = opts.pool === 'review' && this.canUseReview();
    if (opts.pool === 'review' && !this.reviewMode) {
      // 통계 부족 → silent fallback to easy. 사용자에게는 별도 안내 안 함.
      console.warn('[review] 통계 부족으로 easy 풀로 fallback');
    }
    const effectivePool = opts.pool === 'review' && !this.reviewMode ? 'easy' : opts.pool;
    this.pool = filterPool(this.view.countries, effectivePool);
    // 두 모드 중 최대 보기 수(=이름 모드 choiceCount=4)를 풀이 감당 가능해야 함.
    const minPoolSize = Math.max(CONFIG.choiceCount, CONFIG.locationChoiceMax);
    if (this.pool.length < minPoolSize) {
      throw new Error(
        `출제 풀이 너무 작습니다: ${this.pool.length}개 (보기 최대 ${minPoolSize}개 필요)`,
      );
    }
    this.modeSequence = this.buildModeSequence(opts.quizMode, CONFIG.questionsPerRound);
    this.state = this.initialState();
    this.emit();
    this.nextQuestion();
  }

  /** mixed면 절반씩 미리 채우고 셔플, 아니면 같은 모드로 채움. */
  private buildModeSequence(
    quizMode: 'name' | 'location' | 'mixed',
    n: number,
  ): EffectiveQuizMode[] {
    if (quizMode !== 'mixed') {
      return Array.from({ length: n }, () => quizMode);
    }
    const half = Math.floor(n / 2);
    const seq: EffectiveQuizMode[] = [];
    for (let i = 0; i < half; i++) seq.push('name');
    for (let i = 0; i < n - half; i++) seq.push('location');
    for (let i = seq.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = seq[i]!;
      seq[i] = seq[j]!;
      seq[j] = tmp;
    }
    return seq;
  }

  private canUseReview(): boolean {
    return hasReviewableData();
  }

  private nextQuestion(): void {
    if (this.state.questionIndex >= this.state.totalQuestions) {
      this.finish();
      return;
    }
    // 이번 문제의 모드를 먼저 확정 (buildQuestion이 모드별 보기 수를 사용)
    const prevMode = this.state.currentMode;
    const newMode = this.modeSequence[this.state.questionIndex] ?? 'name';
    // 1번 문제(questionIndex === 0)에서는 토스트 없음.
    const modeChanged = this.state.questionIndex > 0 && prevMode !== newMode;
    this.state.currentMode = newMode;
    const question = this.buildQuestion();
    this.state.current = question;
    this.state.phase = 'asking';
    this.emit();
    this.view.highlight.clear();
    if (this.state.currentMode === 'location') {
      // 보기 N개를 모두 노랗게 하이라이트. 정답이 어느 건지 표시하지 않음.
      for (const choice of question.choices) {
        this.view.highlight.set(choice.iso2, CONFIG.colors.highlight);
      }
      // 보기 위에 마우스 올려서 이름 보는 것 방지 (데스크톱). 모든 라벨 가림.
      this.view.setAllLabelsHidden(true);
      this.view.setHiddenLabel(null);
      this.view.refresh();
      this.view.setAutoRotate(false);
      this.view.flyToMany(
        question.choices.map((c) => c.iso2),
        1200,
      );
    } else {
      this.view.highlight.set(question.answer.iso2, CONFIG.colors.highlight);
      this.view.setHiddenLabel(question.answer.iso2);
      this.view.setAllLabelsHidden(false);
      this.view.refresh();
      this.view.setAutoRotate(false);
      this.view.flyTo(question.answer.iso2, 1200);
    }
    this.callbacks.onAsk(question, { modeChanged });
    this.startTimer();
  }

  private buildQuestion(): Question {
    const usedAnswers = new Set(
      this.state.history.map((h) => h.question.answer.iso2),
    );
    let answer: Country | null = null;
    if (this.reviewMode) {
      answer = this.pickReviewAnswer(usedAnswers);
    }
    if (answer === null) {
      const candidates = this.pool.filter((c) => !usedAnswers.has(c.iso2));
      const pickFrom = candidates.length > 0 ? candidates : this.pool;
      answer = pickRandom(pickFrom);
    }

    const sameContinent = this.pool.filter(
      (c) => c.continent === answer!.continent && c.iso2 !== answer!.iso2,
    );
    const others = this.pool.filter(
      (c) => c.continent !== answer!.continent && c.iso2 !== answer!.iso2,
    );
    const totalChoices =
      this.state.currentMode === 'location'
        ? this.randomLocationChoiceCount()
        : CONFIG.choiceCount;
    const wrongCount = totalChoices - 1;
    const wrongs: Country[] = [];
    const continentPool = shuffle(sameContinent);
    for (const c of continentPool) {
      if (wrongs.length >= wrongCount) break;
      wrongs.push(c);
    }
    if (wrongs.length < wrongCount) {
      const fillers = shuffle(others);
      for (const c of fillers) {
        if (wrongs.length >= wrongCount) break;
        wrongs.push(c);
      }
    }
    const choices = shuffle([answer, ...wrongs]);
    return { answer, choices };
  }

  private randomLocationChoiceCount(): number {
    const lo = CONFIG.locationChoiceMin;
    const hi = CONFIG.locationChoiceMax;
    return Math.floor(Math.random() * (hi - lo + 1)) + lo;
  }

  private pickReviewAnswer(usedAnswers: Set<string>): Country | null {
    const eligibleIso = new Set(getEligibleForReview());
    if (eligibleIso.size === 0) return null;
    const candidates = this.view.countries.filter(
      (c) => eligibleIso.has(c.iso2) && !usedAnswers.has(c.iso2),
    );
    if (candidates.length === 0) return null;
    const picked = pickWeighted(candidates, (c) => getReviewWeight(c.iso2));
    return picked;
  }

  submit(picked: Country): void {
    if (this.state.phase !== 'asking' || this.state.current === null) return;
    this.stopTimer();
    const question = this.state.current;
    const correct = picked.iso2 === question.answer.iso2;
    this.applyResult(question, picked, correct);
  }

  /** 위치 모드: 지구본 클릭으로 답을 받는다. 보기 외 폴리곤 클릭은 미스클릭으로 처리. */
  submitByIso2(iso2: string): void {
    if (this.state.phase !== 'asking' || this.state.current === null) return;
    if (this.state.currentMode !== 'location') return;
    const picked = this.state.current.choices.find((c) => c.iso2 === iso2);
    if (!picked) {
      this.callbacks.onMisclick?.();
      return;
    }
    this.stopTimer();
    const question = this.state.current;
    const correct = picked.iso2 === question.answer.iso2;
    this.applyResult(question, picked, correct);
  }

  /** 시간 초과: picked = null로 오답 처리 */
  private handleTimeout(): void {
    if (this.state.phase !== 'asking' || this.state.current === null) return;
    this.stopTimer();
    const question = this.state.current;
    this.applyResult(question, null, false);
  }

  private applyResult(
    question: Question,
    picked: Country | null,
    correct: boolean,
  ): void {
    this.state.history.push({
      question,
      pickedIso2: picked?.iso2 ?? null,
      correct,
    });
    recordResult(question.answer.iso2, correct);
    // 정답이 공개됐으니 hover 라벨도 다시 보여줌
    this.view.setHiddenLabel(null);
    this.view.setAllLabelsHidden(false);
    // 위치 모드의 노란 보기들을 한 번 비우고 결과 색만 다시 칠한다.
    this.view.highlight.clear();
    if (correct) {
      this.state.score += 1;
      this.state.combo += 1;
      if (this.state.combo > this.state.maxCombo) {
        this.state.maxCombo = this.state.combo;
      }
      this.state.phase = 'correct';
      this.view.highlight.set(question.answer.iso2, CONFIG.colors.correct);
    } else {
      this.state.wrongCount += 1;
      this.state.combo = 0;
      this.state.phase = 'wrong';
      // 정답 위치를 파란색으로 보여줌 (M2)
      this.view.highlight.set(question.answer.iso2, CONFIG.colors.answerOnWrong);
      if (picked !== null) {
        this.view.highlight.set(picked.iso2, CONFIG.colors.wrong);
      }
    }
    this.view.refresh();
    this.callbacks.onResult(
      correct,
      picked ?? question.answer,
      question.answer,
    );
    this.emit();

    const delay = correct
      ? CONFIG.betweenQuestionsMs
      : this.state.currentMode === 'location'
        ? CONFIG.locationShowAnswerOnWrongMs
        : CONFIG.showAnswerOnWrongMs;
    this.pendingAdvance = window.setTimeout(() => {
      this.pendingAdvance = null;
      this.advanceQuestion();
    }, delay);
  }

  private advanceQuestion(): void {
    this.state.questionIndex += 1;
    this.state.current = null;
    this.nextQuestion();
  }

  /** 결과 표시 중(correct/wrong) 화면 탭 시 남은 대기를 스킵하고 다음 문제로. */
  skipDelay(): void {
    if (this.pendingAdvance === null) return;
    if (this.state.phase !== 'correct' && this.state.phase !== 'wrong') return;
    window.clearTimeout(this.pendingAdvance);
    this.pendingAdvance = null;
    this.advanceQuestion();
  }

  private startTimer(): void {
    if (!CONFIG.timeLimit.enabled) return;
    this.timeLimitSecondsAtStart = CONFIG.timeLimit.seconds;
    this.timerStart = performance.now();
    this.lastWholeSecond = -1;
    const tick = (): void => {
      if (this.state.phase !== 'asking') return;
      const elapsed = (performance.now() - this.timerStart) / 1000;
      const remaining = Math.max(
        0,
        1 - elapsed / this.timeLimitSecondsAtStart,
      );
      this.callbacks.onTimerTick?.(remaining);
      // 정수 초 경계(4→3, 3→2, 2→1)로 떨어지는 첫 프레임에만 카운트다운 콜백.
      const remainingSec = Math.ceil(remaining * this.timeLimitSecondsAtStart);
      if (
        this.lastWholeSecond !== -1 &&
        remainingSec < this.lastWholeSecond &&
        remainingSec >= 1 &&
        remainingSec <= 3
      ) {
        this.callbacks.onCountdown?.(remainingSec);
      }
      this.lastWholeSecond = remainingSec;
      if (remaining <= 0) {
        this.handleTimeout();
        return;
      }
      this.timerHandle = window.requestAnimationFrame(tick);
    };
    this.timerHandle = window.requestAnimationFrame(tick);
  }

  private stopTimer(): void {
    if (this.timerHandle !== null) {
      window.cancelAnimationFrame(this.timerHandle);
      this.timerHandle = null;
    }
    this.callbacks.onTimerTick?.(1);
  }

  private finish(): void {
    this.state.phase = 'finished';
    recordRoundComplete(this.state.totalQuestions, this.state.score);
    const opts = getOptions();
    const best = recordBest(opts.pool, opts.quizMode, this.state.score);
    this.state.bestUpdate = { ...best, current: this.state.score };
    this.view.highlight.clear();
    this.view.refresh();
    this.view.setAutoRotate(true);
    this.emit();
    this.callbacks.onFinish(this.state);
  }

  private emit(): void {
    this.callbacks.onStateChange(this.state);
  }
}
